import { useState } from 'react'
import { addMeasurement, deleteMeasurement, setHealthGoal } from '../../budget/repo'
import { useStore } from '../../store'
import { Card, TopBar } from '../../ui/parts'
import {
  formatMeasurement,
  formatPct,
  parseMeasurement,
  trackerView,
  type MeasurementKind,
  type TrackerView,
} from '../health'

export function HealthScreen() {
  const { snapshot } = useStore()
  if (!snapshot) return null

  return (
    <div className="screen">
      <TopBar title="Health" subtitle="Body measurements" />

      <div className="stack">
        <Tracker view={trackerView(snapshot.health, 'weight')} />
        <Tracker view={trackerView(snapshot.health, 'booty')} />

        <Card>
          <div className="card-title">Coming later</div>
          <div className="muted" style={{ fontSize: 14, marginTop: 4 }}>
            To add: Whoop, RingConn, Withings integration.
          </div>
        </Card>
      </div>
    </div>
  )
}

function Tracker({ view }: { view: TrackerView }) {
  const { mutate, showToast } = useStore()
  const [value, setValue] = useState('')
  const [goalText, setGoalText] = useState(view.goal === null ? '' : String(view.goal))
  const [editingGoal, setEditingGoal] = useState(false)

  const parsed = parseMeasurement(value)
  const parsedGoal = parseMeasurement(goalText)

  // Direction is not assumed: dropping weight and growing a measurement are both progress,
  // so movement towards the goal is what gets the positive colour.
  const towardsGoal =
    view.changeAbs === null || view.toGoal === null
      ? null
      : Math.sign(view.changeAbs) === Math.sign(view.toGoal) || view.changeAbs === 0

  return (
    <Card>
      <div className="row between">
        <span className="lbl">{view.label}</span>
        <span className="lbl">
          {view.currentDate ? `Last ${view.currentDate}` : 'No readings yet'}
        </span>
      </div>

      <div className="row" style={{ alignItems: 'baseline', gap: 10 }}>
        <span className="big">{formatMeasurement(view.current, view.unit)}</span>
        {view.changePct !== null ? (
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color:
                towardsGoal === null
                  ? 'var(--ink-muted)'
                  : towardsGoal
                    ? 'var(--rec-high)'
                    : 'var(--rec-low)',
            }}
          >
            {formatPct(view.changePct)}
          </span>
        ) : null}
      </div>

      <div className="muted" style={{ fontSize: 13 }}>
        {view.goal === null ? (
          'No goal set'
        ) : (
          <>
            Goal {formatMeasurement(view.goal, view.unit)}
            {view.toGoal !== null ? (
              <>
                {' · '}
                {Math.abs(Math.round(view.toGoal * 10) / 10)} {view.unit} to go
              </>
            ) : null}
          </>
        )}
        {view.changePct !== null ? ` · over ${view.windowDays} days` : ''}
      </div>

      <div className="row" style={{ marginTop: 12 }}>
        <input
          type="text"
          inputMode="decimal"
          placeholder={`Today's ${view.label.toLowerCase()} (${view.unit})`}
          value={value}
          className={value !== '' && parsed === null ? 'error' : ''}
          onChange={(e) => setValue(e.target.value)}
          style={{ flex: 1 }}
        />
        <button
          type="button"
          className="btn small"
          disabled={parsed === null}
          onClick={async () => {
            if (parsed === null) return
            if (await mutate((s) => addMeasurement(s, view.kind, parsed))) {
              setValue('')
              showToast(`${view.label} logged`)
            }
          }}
        >
          Log
        </button>
      </div>

      {editingGoal ? (
        <div className="row" style={{ marginTop: 8 }}>
          <input
            type="text"
            inputMode="decimal"
            placeholder={`Goal (${view.unit})`}
            value={goalText}
            onChange={(e) => setGoalText(e.target.value)}
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className="btn small"
            onClick={async () => {
              if (await mutate((s) => setHealthGoal(s, view.kind, parsedGoal))) {
                setEditingGoal(false)
              }
            }}
          >
            Save
          </button>
          <button type="button" className="btn ghost small" onClick={() => setEditingGoal(false)}>
            Cancel
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="btn ghost small"
          style={{ paddingLeft: 0, marginTop: 4 }}
          onClick={() => setEditingGoal(true)}
        >
          {view.goal === null ? 'Set a goal' : 'Change goal'}
        </button>
      )}

      {view.history.length > 0 ? (
        <div style={{ marginTop: 10, borderTop: '1px solid var(--hairline)', paddingTop: 6 }}>
          {view.history.slice(0, 6).map((entry) => (
            <div className="row between" key={entry.id} style={{ padding: '5px 0' }}>
              <span className="muted" style={{ fontSize: 13 }}>
                {entry.date}
              </span>
              <span className="tnum" style={{ fontSize: 14 }}>
                {formatMeasurement(entry.value, view.unit)}
              </span>
              <button
                type="button"
                className="icon-btn"
                aria-label="Delete reading"
                onClick={() => void mutate((s) => deleteMeasurement(s, entry.id))}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </Card>
  )
}

export type { MeasurementKind }
